import { useGqlMutation, useGqlQuery } from '@API/useAvQuery';
import { ResponseHandler } from '@components/formInputTypes';
import { OltpPagination } from '@fabric-ui/components/Table';
import { QueryKey } from '@fabric-ui/utils';
import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { Outegration } from '@views/Outegrations/types';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { RunInfo } from '../../context/AvSnackBarProvider';
import useOLTPPagination from '../../hooks/useOLTPPagination';
import { PermissionEntitiesNames } from '../../types';
import { getHeadCellsWithSort } from '../Tickets/hooks';
import { DatastoreMappingItem, TGateway, ZscalerAccountConfigObject } from './types';
import { getHeadCells } from './utils';

export const useAccountDatastoreMapping = accountId =>
  useGqlQuery<DatastoreMappingItem[]>({
    queryKey: [QueryKey.FindAccountDatastoresMapping, accountId],
    query: GET_ACCOUNT_DATASTORE_MAPPING,
    enabled: !!accountId,
  });

export const useAvailableServiceNames = () =>
  useGqlQuery<string[]>({
    queryKey: [QueryKey.GetAvailableServiceNames],
    query: GET_SERVICE_NAMES,
  });

export const useAvailableDatastoreCategories = () =>
  useGqlQuery<string[]>({
    queryKey: [QueryKey.GetAvailableDatastoreCategories],
    query: GET_CATEGORIES_NAMES,
  });

export const UseDatastoreMappingMutation = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: updateDatastoreMapping } = useMutation({
    mutationFn: (accountDatastoreMapping: DatastoreMappingItem) =>
      api(UPDATE_DATASTORE_MAPPING, {
        options: { accountDatastoreMapping },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to update mapping ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Updated mapping successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  const { mutateAsync: createDatastoreMapping } = useMutation({
    mutationFn: (accountDatastoreMapping: DatastoreMappingItem) =>
      api(CREATE_DATASTORE_MAPPING, {
        options: { accountDatastoreMapping },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to create mapping ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Created mapping successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  const { mutateAsync: deleteDatastoreMapping } = useMutation({
    mutationFn: (accountDatastoreMappingId: string | undefined) =>
      api(DELETE_DATASTORE_MAPPING, {
        options: { accountDatastoreMappingId },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to delete mapping ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Deleted mapping successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  return { updateDatastoreMapping, createDatastoreMapping, deleteDatastoreMapping };
};

export const useGetSaml = ({ domain, onSuccess, options }) =>
  useGqlQuery({
    queryKey: [QueryKey.SamlDetails, domain],
    query: GET_SAML_CREDS,
    variables: { emailDomain: domain },
    onSuccess: data => onSuccess(data),
    ...options,
  });

export const useEditAccount = ({ isEditMode }) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: ({ accountDto, onError, onSuccess }: { accountDto; onError; onSuccess }) =>
      api(isEditMode ? UPDATE_ACCOUNT : CREATE_ACCOUNT, { options: { accountDto }, muteErrors: true, onSuccess }).catch(onError),
  });
};

export const useZidAccountSync = () =>
  useGqlMutation<void, { accountId: string }>({
    mutationKey: [QueryKey.ZidAccountSync],
    mutation: ZID_ACCOUNT_SYNC,
  });

export const useGetAccount = ({
  id,
  onSuccess = data => data,
  options,
}: {
  id: string;
  onSuccess?: (data: any) => any;
  options?: object;
}) =>
  useGqlQuery({
    queryKey: [QueryKey.FindAccountById, id],
    query: GET_ACCOUNT,
    variables: { id },
    onSuccess,
    ...options,
  });

export const useGetAllDashboardAccessRoles = () =>
  useGqlQuery<any[]>({
    queryKey: [QueryKey.FindAllDashboardAccessRoles],
    query: GET_ACCOUNT_DASHBOARD_ACCESS_ROLES,
  });

export const useGetGateways = ({ onSuccess = d => d, options = {} }) =>
  useGqlQuery<TGateway[]>({
    queryKey: [QueryKey.Gateways],
    query: GET_GATEWAYS,
    gcTime: 0,
    placeholderData: keepPreviousData,
    onSuccess: d => onSuccess(d),
    ...options,
  });

export const timezonesList = (() => {
  const tzList = Intl.supportedValuesOf('timeZone');

  const tzArray = [
    { title: 'User Browser Timezone', value: null },
    { title: 'UTC', value: 'UTC' },
    ...tzList.reduce(
      (list, tz) => {
        const offset =
          new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
            .formatToParts()
            .find(part => part.type === 'timeZoneName')
            ?.value.replace('GMT', 'UTC') ?? '';
        if (tz === 'UTC') {
          return list;
        }
        const [region, ...rest] = tz.split('/');
        const city = rest.reverse().join(', ').replace('_', ' ');
        const title = `${city}, ${region} (${offset})`;
        return [...list, { title, value: tz }];
      },
      [] as { title: string; value: string }[]
    ),
  ];

  return tzArray.sort((a, b) => a.title.localeCompare(b.title));
})();

export const useGatewayMutation = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: updateGateway } = useMutation({
    mutationFn: (gatewayDto: TGateway) =>
      api(UPDATE_GATEWAY, {
        options: { gatewayDto },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to update gateway ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Updated gateway successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  const { mutateAsync: createGateway } = useMutation({
    mutationFn: (gatewayDto: TGateway) =>
      api(CREATE_GATEWAY, {
        options: { gatewayDto },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to create gateway ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Created gateway successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  const { mutateAsync: deleteGateway } = useMutation({
    mutationFn: (id: string | undefined) =>
      api(DELETE_GATEWAYS, {
        options: { id },
        isWebserver: false,
        onError: () => enqueueSnackbar('Failed to delete gateway ', { variant: 'error' }),
      }).then(() => {
        enqueueSnackbar('Deleted gateway successfully ', { variant: 'success' });
        onSuccess();
      }),
  });

  return { updateGateway, createGateway, deleteGateway };
};

export const validateBeforeSaveZscalerConfig = config => {
  const newConfig: ZscalerAccountConfigObject = { zpaTenantsConfigs: [], ziaTenantsConfigs: [] };
  if (config.ziaTenantsConfigs?.[0]?.cloudName || config.ziaTenantsConfigs?.[0]?.companyId) {
    newConfig.ziaTenantsConfigs = config.ziaTenantsConfigs;
  }
  if (config.zpaTenantsConfigs?.[0]?.cloudName || config.zpaTenantsConfigs?.[0]?.tenantId) {
    newConfig.zpaTenantsConfigs = config.zpaTenantsConfigs;
  }
  if (config.zscmAccountId) {
    newConfig.zscmAccountId = config.zscmAccountId;
  }
  return newConfig;
};

export const validateBeforeCreateZscalerConfig = config => {
  const newConfig = config;
  if (!config) {
    return defaultZscalerConfig;
  }
  if (config.ziaTenantsConfigs?.length === 0) {
    newConfig.ziaTenantsConfigs = defaultZscalerConfig.ziaTenantsConfigs;
  }
  if (config.zpaTenantsConfigs?.length === 0) {
    newConfig.zpaTenantsConfigs = defaultZscalerConfig.zpaTenantsConfigs;
  }
  if (!config.zscmAccountId) {
    newConfig.zscmAccountId = defaultZscalerConfig.zscmAccountId;
  }
  return newConfig;
};

export const defaultZscalerConfig = {
  zscmAccountId: '',
  ziaTenantsConfigs: [{ cloudName: '', companyId: '' }],
  zpaTenantsConfigs: [{ cloudName: '', tenantId: '' }],
};

export const defaultAiFeatures = {
  enabled: true,
};

interface Props {
  reset: VoidFunction;
  handleResponse?: ResponseHandler;
  setRunInfo: (runInfo: RunInfo, shouldDelete?: boolean | undefined) => void;
  onSuccessNext?: VoidFunction;
  setResponseValues?: (data: any) => void;
}

export const useOnSuccessAccountManagement = ({ reset, handleResponse, setRunInfo, onSuccessNext, setResponseValues }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return res => {
    const resKey = Object.keys(res.data)[0];

    if (handleResponse === ResponseHandler.DEFAULT_MAPPING) {
      if (res.data[resKey]) {
        enqueueSnackbar('The Mapping is valid, default mapping PR created!', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to validate and create default mapping PR.', { variant: 'error' });
      }
      return;
    }

    enqueueSnackbar(`Successfully Saved`, { variant: 'success' });
    reset();
    if (handleResponse === ResponseHandler.NONE) {
      onSuccessNext?.();
      return;
    }
    if (handleResponse === ResponseHandler.WORKFLOW) {
      if (res.data[resKey].workflowRun.runId && res.data[resKey].workflowRun.id) {
        setRunInfo({
          runId: res.data[resKey].workflowRun.runId,
          wfId: res.data[resKey].workflowRun.id,
          returnStatusOnly: true,
          onSuccess: onSuccessNext,
        });
      }
    } else if (handleResponse === ResponseHandler.ADD_ACCOUNT) {
      queryClient.refetchQueries({ queryKey: ['userAdditionalInfo'] });
    } else if (handleResponse === ResponseHandler.API_ACCESS) {
      if (setResponseValues) {
        setResponseValues(res.data[resKey]);
      }
    }
  };
};

export const useAccountManagement = () => {
  const { pageNumber, setPageNumber, parsedOrderBy, filter, onSort, sort } = useOLTPPagination();

  const {
    data: paginationData = { content: [], totalElements: 0 },
    isLoading,
    refetch: refetchAccounts,
    isRefetching,
  } = useAccountsList({ filter, pageNumber, sort });
  const { content: rows, totalElements } = paginationData;
  const headCells = getHeadCellsWithSort(getHeadCells(filter), parsedOrderBy);

  return {
    headCells,
    rows,
    isLoading,
    refetchAccounts,
    totalElements,
    isRefetching,
    pageNumber,
    setPageNumber,
    parsedOrderBy,
    filter,
    onSort,
  };
};

export const useAccountsList = ({ filter, pageNumber, sort }) =>
  useGqlQuery<OltpPagination<any>>({
    queryKey: [QueryKey.FindAllAccounts, pageNumber, sort, filter],
    query: GET_FULL_LIST_PAGINATION,
    variables: {
      filterablePage: {
        filter,
        pageable: {
          pageNumber,
          pageSize: 20,
          ...sort,
        },
      },
    },
    select: data => data || { content: [], totalElements: 0 },
    placeholderData: keepPreviousData,
  });

export const useGetExportableEntityTypes = ({ enabled }) =>
  useGqlQuery<any>({
    queryKey: [QueryKey.GetExportableEntityTypes],
    query: GET_EXPORTABLE_ENTITY_TYPES,
    enabled,
  });

export const useGetS3Outegrations = ({ enabled, select }) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  return useGqlQuery<Outegration[]>({
    queryKey: [QueryKey.Outegrations, 'S3'],
    query: GET_S3_OUTEGRATIONS,
    enabled: hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.INTEGRATION }) && enabled,
    select,
  });
};

export const GET_ACCOUNT = gql`
  query findAccountById($id: String!) {
    findAccountById(id: $id) {
      id
      name
      logo
      industry
      accountType
      authenticationType
      accountLimits {
        assetCount
        revenueId
        maxDomains
      }
      emailDomain
      xmlMetadata
      identityProviderName
      timezone
      crmId
      apps {
        id
        name
      }
      idleTimeForLogout
      zscalerCustomerConfig {
        zscmAccountId
        ziaTenantsConfigs {
          cloudName
          companyId
        }
        zpaTenantsConfigs {
          cloudName
          tenantId
        }
      }
      accountDashboardAccessRoles {
        id
        name
      }
      auditLogsExportConfig {
        enabled
        includedEntities
        integrationId
        cronExpression
      }
      aiFeatures {
        enabled
      }
    }
  }
`;

export const CREATE_ACCOUNT = gql`
  mutation createAccount($accountDto: AccountDtoInput!) {
    createAccount(accountDto: $accountDto) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const GET_ACCOUNT_DASHBOARD_ACCESS_ROLES = gql`
  query findAllDashboardAccessRoles {
    findAllDashboardAccessRoles {
      id
      name
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  mutation updateAccount($accountDto: AccountDtoInput!) {
    updateAccount(accountDto: $accountDto) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const GET_SAML_CREDS = gql`
  query getSamlCreds($emailDomain: String!) {
    getSamlCreds(emailDomain: $emailDomain) {
      entityId
      replyUrl
    }
  }
`;

export const GET_ACCOUNT_DATASTORE_MAPPING = gql`
  query findAccountDatastoresMapping {
    findAccountDatastoresMapping {
      id
      accountId
      datastoreCategory
      datastoreId
      serviceName
      datastoreConfig
    }
  }
`;

export const UPDATE_DATASTORE_MAPPING = gql`
  mutation updateDatastoreMapping($accountDatastoreMapping: AccountDatastoreMappingDto!) {
    updateDatastoreMapping(accountDatastoreMapping: $accountDatastoreMapping) {
      id
      accountId
    }
  }
`;

export const CREATE_DATASTORE_MAPPING = gql`
  mutation createAccountDatastoreMapping($accountDatastoreMapping: AccountDatastoreMappingDto!) {
    createAccountDatastoreMapping(accountDatastoreMapping: $accountDatastoreMapping) {
      id
      accountId
    }
  }
`;

export const DELETE_DATASTORE_MAPPING = gql`
  mutation deleteAccountDatastoreMappingById($accountDatastoreMappingId: String!) {
    deleteAccountDatastoreMappingById(accountDatastoreMappingId: $accountDatastoreMappingId)
  }
`;

export const GET_SERVICE_NAMES = gql`
  query getAvailableServiceNames {
    getAvailableServiceNames
  }
`;

export const GET_CATEGORIES_NAMES = gql`
  query getAvailableDatastoreCategories {
    getAvailableDatastoreCategories
  }
`;

export const GET_FULL_LIST_PAGINATION = gql`
  query findAllAccountsPagination($useAccountRestrictedFilter: Boolean, $filterablePage: FilterablePage) {
    findAllAccountsPagination(useAccountRestrictedFilter: $useAccountRestrictedFilter, filterablePage: $filterablePage) {
      content {
        id
        name
        logo
        industry
        accountType
        apps {
          name
          id
        }
      }
      totalPages
      totalElements
      number
      last
    }
  }
`;

export const GET_FILTERED_LIST = gql`
  query findAllAccounts {
    findAllAccounts(useAccountRestrictedFilter: true) {
      id
      name
      logo
      industry
      accountType
    }
  }
`;

export const GET_API_SCOPES = gql`
  query getApiScopes {
    getApiScopes
  }
`;

export const UPDATE_CUSTOMER_PROVIDER = gql`
  mutation updateCustomerAuthProvider($accountId: String, $authenticationProvider: AuthenticationProvider!) {
    updateCustomerAuthProvider(accountId: $accountId, authenticationProvider: $authenticationProvider)
  }
`;

export const GET_MAIN_ACCOUNT_BY_AUTH_PROVIDER = gql`
  query findMainAccountsByProvider($useAccountRestrictedFilter: Boolean!, $authenticationProvider: AuthenticationProvider!) {
    findMainAccountsByProvider(useAccountRestrictedFilter: $useAccountRestrictedFilter, authenticationProvider: $authenticationProvider) {
      id
      name
      logo
      industry
      accountType
    }
  }
`;

export const GET_FILTERED_LIST_RISK360 = gql`
  query findAllRisk360Accounts {
    findAllRisk360Accounts(useAccountRestrictedFilter: true) {
      id
      name
      logo
      industry
      accountType
    }
  }
`;

export const GET_ALL_ETL_MONITORS_LIST = gql`
  query findAllEtlMonitors {
    findAllEtlMonitors {
      monitorName
      accountId
      id
    }
  }
`;

export const GET_EXPORTABLE_ENTITY_TYPES = gql`
  query getExportableEntityTypes {
    getExportableEntityTypes {
      name
      displayName
    }
  }
`;

export const GET_S3_OUTEGRATIONS = gql`
  query findAvailableOutegrationInstancesForAudit {
    findAvailableOutegrationInstancesForAudit {
      id
      name
    }
  }
`;
export const DELETE_ACCOUNTS = gql`
  mutation deleteAccountByIds($ids: [String!]!) {
    deleteAccountByIds(ids: $ids) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

const GET_GATEWAYS = gql`
  query getGateways {
    getGateways {
      id
      name
      proxyDnsName
      publicKey
      privateDnsName
    }
  }
`;

export const CREATE_GATEWAY = gql`
  mutation createGateway($gatewayDto: createGatewayDtoInput!) {
    createGateway(gatewayDto: $gatewayDto) {
      id
      name
      proxyDnsName
      publicKey
      privateDnsName
    }
  }
`;
export const UPDATE_GATEWAY = gql`
  mutation updateGateway($gatewayDto: GatewayDtoInput!) {
    updateGateway(gatewayDto: $gatewayDto) {
      id
      name
      proxyDnsName
      publicKey
      privateDnsName
    }
  }
`;

export const DELETE_GATEWAYS = gql`
  mutation deleteGateway($id: String!) {
    deleteGateway(id: $id) {
      id
      name
      proxyDnsName
      publicKey
      privateDnsName
    }
  }
`;

export const ZID_ACCOUNT_SYNC = gql`
  mutation zidAccountSync($accountId: String!) {
    zidAccountSync(accountId: $accountId)
  }
`;
